import { ErrorHandler, Injectable } from '@angular/core';
import { WindowRefService } from '@rd/core/services/window-ref/window-ref.service';
import * as Sentry from '@sentry/angular';
import { Observable, Subject } from 'rxjs';
import { filter, map, scan } from 'rxjs/operators';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  errorSubject = new Subject<Error>();
  errorStream$: Observable<any>;

  constructor(private windowRef: WindowRefService) {
    this.errorStream$ = this.errorSubject.pipe(
      this.throttleDistinct(1000, this.isErrorEqual),
    );

    this.errorStream$.subscribe((error: Error | any) => {
      const cause = error.originalError || error;
      console.error(cause);
      Sentry.captureException(cause);
    });
  }

  private throttleDistinct<T>(
    duration: number,
    equals: (a: T, b: T) => boolean,
  ): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) => {
      return source.pipe(
        map((x) => {
          const obj = { val: x, time: Date.now(), keep: true };
          return obj;
        }),
        scan((acc, cur) => {
          const diff = cur.time - acc.time;

          const isSame = equals(acc.val, cur.val);

          if (diff < duration && isSame) {
            return { ...acc, time: Date.now(), keep: false };
          }

          return diff > duration || (diff < duration && !isSame)
            ? { ...cur, keep: true }
            : { ...acc, keep: false };
        }),
        filter((x) => x.keep),
        map((x) => x.val),
      );
    };
  }

  handleError(err: Error): void {
    // Chunk load failures usually happen after a deploy when the user's
    // tab references a code-split chunk that no longer exists. Reload
    // recovers them instead of leaving the app in a broken state.
    const chunkFailedMessage = /Loading chunk .+ failed/;

    if (chunkFailedMessage.test(err.message)) {
      this.windowRef.nativeWindow.location.reload();

      return;
    }

    this.errorSubject.next(err);
  }

  private isErrorEqual(err1: Error, err2: Error): boolean {
    return (
      err1.stack === err2.stack &&
      err1.message === err2.message &&
      err1.name === err2.name
    );
  }
}
